import styled, { css } from 'styled-components';
import { injectTypography } from '../../utils/injectTypography';
import { injectSpacing } from '../../api/spacing/injectSpacing';
import { injectOSXFontSmoothing } from '../../utils/injectOSXFontSmoothing';
// All values are pixel values, units are omitted for calculations.
const containerWidth = 58;
const containerHeight = 32;
const containerPadding = 3;
const switchIdleSize = 26;
const switchHoverSize = 20;
const switchHoverScaleFactor = switchHoverSize / switchIdleSize;
const hoverEffectDuration = '150ms';
const toggleEffectDuration = '250ms';
export const StyledContainer = styled.div `
  ${injectSpacing('margin')}

  display: flex;
  align-items: center;
`;
export const StyledToggleContainer = styled.span `
  position: relative;

  display: grid;
  flex-shrink: 0;
  grid-template-columns: repeat(2, 1fr);

  box-sizing: border-box;

  width: ${containerWidth + 'px'};
  height: ${containerHeight + 'px'};
`;
export const StyledLabel = styled.label `
  ${(props) => injectTypography(props, 'copy1')}
  ${injectOSXFontSmoothing(true)}

  color: ${(props) => props.theme.colors.form.primary};
  margin-inline-start: var(${(props) => props.theme.responsive.spacing.s});

  cursor: pointer;

  transition: color ${hoverEffectDuration} ${(props) => props.theme.easing};

  ${(props) => props.disabled
    ? css `
          color: ${(props) => props.theme.colors.form.muted.secondary};
        `
    : css `
          &:hover,
          ${StyledToggleContainer}:hover ~ & {
            color: ${(props) => props.theme.colors.form.strong};
          }
        `}
`;
export const StyledToggleButton = styled.button `
  grid-row: 1;
  grid-column: 1 / 3;

  margin: 0;
  padding: 0;

  background-color: ${(props) => props.theme.colors.option.secondary};

  border: none;

  border-radius: ${containerHeight / 2 + 'px'};
  outline-offset: var(${(props) => props.theme.responsive.spacing.xxs});

  cursor: pointer;

  transition: ${toggleEffectDuration} ${(props) => props.theme.easing};
  transition-property: background-color;

  appearance: none;

  &:disabled {
    background-color: ${(props) => props.theme.colors.option.disabled.secondary};
  }

  &[aria-checked='true'] {
    background-color: ${(props) => props.theme.colors.option.primary};
  }

  &[aria-checked='true']:disabled {
    background-color: ${(props) => props.theme.colors.option.disabled.primary};
  }
`;
export const StyledToggleSwitch = styled.div `
  position: relative;

  grid-row: 1;
  grid-column: 1 / 3;

  pointer-events: none;

  &::after {
    position: absolute;

    top: ${containerPadding + 'px'};

    width: ${switchIdleSize + 'px'};
    height: ${switchIdleSize + 'px'};

    background-color: ${(props) => props.theme.colors.option.tertiary};

    border-radius: 50%;
    transform-origin: center;

    transition: left ${toggleEffectDuration} ${(props) => props.theme.easing},
      transform ${hoverEffectDuration} ${(props) => props.theme.easing};

    content: '';
  }

  ${StyledToggleButton}[aria-checked='false'] ~ &::after,
  [dir='rtl'] ${StyledToggleButton}[aria-checked='true'] ~ &::after {
    left: ${containerPadding + 'px'};
  }

  ${StyledToggleButton}[aria-checked='true'] ~ &::after,
  [dir='rtl'] ${StyledToggleButton}[aria-checked='false'] ~ &::after {
    left: calc(100% - ${switchIdleSize + 'px'} - ${containerPadding + 'px'});
  }

  ${StyledToggleButton}:disabled ~ &::after {
    background-color: ${(props) => props.theme.colors.option.disabled.tertiary};
  }

  ${StyledToggleButton}:not(:disabled):hover ~ &::after {
    transform: scale(${switchHoverScaleFactor});
  }
`;
export const StyledOnIndicator = styled.div `
  grid-row: 1;
  grid-column: 1;

  align-self: center;
  justify-self: center;

  margin-inline-start: ${containerPadding + 'px'};

  width: 1px;
  height: 8px;

  background: ${(props) => props.theme.colors.utility.white};

  transition: transform ${toggleEffectDuration} ${(props) => props.theme.easing},
    opacity ${hoverEffectDuration} ${(props) => props.theme.easing};

  ${StyledToggleButton}:disabled ~ & {
    opacity: 0;
  }

  pointer-events: none;

  ${StyledToggleButton}[aria-checked='false'] ~ & {
    transform: translateX(${switchIdleSize + 'px'});
    opacity: 0;
  }

  [dir='rtl'] ${StyledToggleButton}[aria-checked='false'] ~ & {
    transform: translateX(${switchIdleSize * -1 + 'px'});
  }
`;
export const StyledOffIndicator = styled.div `
  grid-row: 1;
  grid-column: 2;

  align-self: center;
  justify-self: center;

  box-sizing: border-box;

  width: 6px;
  height: 6px;

  margin-inline-end: ${containerPadding + 'px'};

  border: solid 1px ${(props) => props.theme.colors.utility.neutral[50]};
  border-radius: 50%;

  transition: transform ${toggleEffectDuration} ${(props) => props.theme.easing},
    opacity ${hoverEffectDuration} ${(props) => props.theme.easing};

  pointer-events: none;

  ${StyledToggleButton}:disabled ~ & {
    opacity: 0;
  }

  ${StyledToggleButton}[aria-checked='true'] ~ & {
    transform: translateX(${switchIdleSize * -1 + 'px'});
    opacity: 0;
  }

  [dir='rtl'] ${StyledToggleButton}[aria-checked='true'] ~ & {
    transform: translateX(${switchIdleSize + 'px'});
  }
`;
//# sourceMappingURL=Toggle.styles.js.map